// src/pages/ProfilePage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail, signOut, updateProfile } from 'firebase/auth';
import { db, getAuthClient } from '../firebaseConfig';
import { useAuth } from '../auth/AuthProvider';
import { normalizeClaims } from '../auth/landing';

export default function ProfilePage() {
  const { user, claims } = useAuth();
  const norm = normalizeClaims(claims || {});
  const orgId = norm.orgId || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');

  // Delete my admin access modal
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [deleting, setDeleting] = useState(false);

  const memberRef = useMemo(() => {
    if (!orgId || !user?.uid) return null;
    return doc(db, 'orgs', orgId, 'members', user.uid);
  }, [orgId, user?.uid]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!memberRef) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const snap = await getDoc(memberRef);
        const d = snap.exists() ? (snap.data() || {}) : {};
        if (cancelled) return;
        setFirstName(String(d.firstName || '').trim());
        setLastName(String(d.lastName || '').trim());
        setDisplayName(String(d.displayName || user?.displayName || '').trim());
      } catch {
        if (!cancelled) {
          setFirstName('');
          setLastName('');
          setDisplayName(String(user?.displayName || '').trim());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [memberRef, user?.displayName]);

  const computedDisplay = useMemo(() => {
    const dn = String(displayName || '').trim();
    if (dn) return dn;
    const fn = String(firstName || '').trim();
    const ln = String(lastName || '').trim();
    return [fn, ln].filter(Boolean).join(' ').trim();
  }, [displayName, firstName, lastName]);

  const saveProfile = async () => {
    if (!user?.uid || !orgId) return;
    setSaving(true);
    setStatus('');
    try {
      const fn = String(firstName || '').trim();
      const ln = String(lastName || '').trim();
      const dn = String(computedDisplay || '').trim();

      // 1) Org member profile (used by Super Admin + audit)
      await setDoc(
        doc(db, 'orgs', orgId, 'members', user.uid),
        {
          uid: user.uid,
          email: user.email || '',
          firstName: fn,
          lastName: ln,
          displayName: dn,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // 2) cashup_users name (used in Labor filters + other places)
      await setDoc(
        doc(db, 'orgs', orgId, 'cashup_users', user.uid),
        {
          name: dn,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // 3) Auth profile displayName (nice-to-have)
      try {
        await updateProfile(user, { displayName: dn });
      } catch {
        // ignore; Firestore remains source of truth
      }

      setStatus('Saved.');
    } catch (e) {
      console.error('saveProfile failed', e);
      setStatus('Could not save profile.');
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(''), 2500);
    }
  };

  const doPasswordReset = async () => {
    if (!user?.email) return;
    setStatus('');
    try {
      const auth = getAuthClient();
      await sendPasswordResetEmail(auth, user.email);
      setStatus('Password reset email sent.');
    } catch (e) {
      console.error('password reset failed', e);
      setStatus('Could not send reset email.');
    } finally {
      setTimeout(() => setStatus(''), 3000);
    }
  };

  const openSelfDisable = () => {
    if (!user || deleting) return;
    setPwErr('');
    setPw('');
    setPwOpen(true);
  };

  const confirmSelfDisable = async () => {
    if (!user || deleting) return;
    const email = user.email;
    const password = String(pw || '');

    if (!email) {
      setPwErr('This account has no email attached. Please log in again with email/password.');
      return;
    }
    if (!password.trim()) {
      setPwErr('Please enter your password to confirm.');
      return;
    }

    try {
      setDeleting(true);
      setPwErr('');

      const auth = getAuthClient();
      const cred = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(auth.currentUser, cred);

      // Disable access
      await setDoc(
        doc(db, 'userRoles', user.uid),
        {
          disabled: true,
          orgId: null,
          apps: [],
          role: 'disabled',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPwOpen(false);
      await signOut(auth);
      window.location.replace('/login');
    } catch (e) {
      console.error('self-disable failed:', e);
      const msg = (e && (e.code || e.message)) ? String(e.code || e.message) : '';
      if (msg.includes('auth/wrong-password') || msg.includes('auth/invalid-credential')) {
        setPwErr('Incorrect password. Please try again.');
      } else if (msg.includes('auth/too-many-requests')) {
        setPwErr('Too many attempts. Please wait a bit and try again.');
      } else {
        setPwErr('Could not verify your password. Please try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-semibold mb-4">Profile</h1>

      {!orgId && (
        <div className="rounded bg-rose-900/20 border border-rose-700/50 px-3 py-2 text-rose-200 mb-4">
          No <code>orgId</code> found on your token; contact the admin.
        </div>
      )}

      <div className="rounded-xl border border-white/10 p-4" style={{ backgroundColor: 'var(--brand-surface)' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-1 text-white/70">First name</label>
            <input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2 rounded border border-white/10 bg-black/20 outline-none"
              placeholder="First name"
              disabled={loading || !orgId}
            />
          </div>
          <div>
            <label className="block text-sm mb-1 text-white/70">Last name</label>
            <input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2 rounded border border-white/10 bg-black/20 outline-none"
              placeholder="Last name"
              disabled={loading || !orgId}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm mb-1 text-white/70">Display name (shown across Admin + Super Admin)</label>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-3 py-2 rounded border border-white/10 bg-black/20 outline-none"
              placeholder="Display name"
              disabled={loading || !orgId}
            />
            <div className="text-xs text-white/50 mt-1">If left blank, we’ll use “First + Last”.</div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={saveProfile}
            disabled={saving || loading || !orgId}
            className="px-4 py-2 rounded text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--brand-accent)' }}
          >
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
          <button
            onClick={doPasswordReset}
            className="px-4 py-2 rounded border border-white/10 hover:bg-white/5"
            disabled={!user?.email}
          >
            Send password reset email
          </button>
          {status && <div className="text-sm text-white/70">{status}</div>}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/70">
          <div>
            <div className="text-white/50">Email</div>
            <div className="text-white">{user.email || '—'}</div>
          </div>
          <div>
            <div className="text-white/50">User ID</div>
            <div className="text-white break-all">{user.uid}</div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-red-500/30 p-4" style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}>
        <div className="font-semibold text-red-200">Danger zone</div>
        <div className="text-sm text-red-100/80 mt-1">
          Remove your access to the Neo Panel. You will be signed out immediately.
        </div>
        <button
          onClick={openSelfDisable}
          className="mt-3 px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
          disabled={deleting}
        >
          Delete my admin access
        </button>
      </div>

      {/* Confirm: Delete my admin access (requires password) */}
      {pwOpen && (
        <div aria-modal="true" role="dialog" className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => {
              if (!deleting) setPwOpen(false);
            }}
          />
          <div
            className="relative w-full max-w-md rounded-lg shadow-xl p-5"
            style={{ background: 'var(--brand-surface, #121a26)', color: 'var(--brand-text, #e6edf7)' }}
          >
            <h3 className="text-lg font-semibold mb-2">Delete my admin access?</h3>
            <p className="text-sm opacity-80 mb-4">
              This will remove your access to the Neo Panel and sign you out immediately.
            </p>

            <label className="block text-sm mb-1 opacity-90">Enter your password to confirm</label>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full px-3 py-2 rounded border border-white/10 bg-black/20 outline-none focus:ring-2 focus:ring-cyan-400/40"
              placeholder="Password"
              autoFocus
            />
            {pwErr && <div className="text-sm text-red-300 mt-2">{pwErr}</div>}

            <div className="flex justify-end gap-2 mt-5">
              <button
                className="px-3 py-2 rounded bg-slate-600 hover:bg-slate-500 disabled:opacity-60"
                disabled={deleting}
                onClick={() => setPwOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 rounded text-white disabled:opacity-60"
                style={{ backgroundColor: '#dc2626' }}
                disabled={deleting}
                onClick={confirmSelfDisable}
              >
                {deleting ? 'Confirming…' : 'Delete access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
